import {tableAnatomy as chakraAnatomy} from '@chakra-ui/anatomy';
import {createMultiStyleConfigHelpers} from '@chakra-ui/styled-system';
import {useMultiStyleConfig} from '@chakra-ui/system';

import {tableCSSVars} from './CSSVars';
import {globalActionsStyle} from './GlobalActions';
import {tdCssVars, tdStyle} from './Td';
import {theadCssVars, theadStyle} from './Thead';
import {thCssVars, thStyle} from './Th';
import {trStyle} from './Tr';

const tableAnatomy = [
  ...chakraAnatomy.keys,
  `wrapper`,
  `header`,
  `rowActions`,
  `globalActions`,
  `rowsWrapper`,
] as const;

const {defineMultiStyleConfig} = createMultiStyleConfigHelpers(tableAnatomy);

const {radii: $radii, padding: $padding} = tableCSSVars;

const TableTheme = defineMultiStyleConfig({
  baseStyle: props => ({
    wrapper: {
      ...tdCssVars(props),
      ...thCssVars(props),
      ...theadCssVars(props),
      width: `full`,
      height: `full`,
      [$radii.variable]: `radii.lg`,
      [$padding.variable]: `1rem`,
    },
    header: {
      display: `flex`,
      alignItems: `flex-start`,
      justifyContent: `space-between`,
      gap: `space-200`,
      paddingBottom: `space-200`,
    },
    rowsWrapper: {
      overflow: `auto`,
      borderRadius: $radii.reference,
    },
    table: {
      borderCollapse: `collapse`,
      // Using table layout fixed require us to define absolute width for each column
      // Otherwise, the column width will be calculated based on the content (same as using `auto`)
      tableLayout: `fixed`,
      fontVariantNumeric: `normal`,
    },
    th: thStyle.baseStyle?.(props),
    caption: {},
    tbody: {},
    td: tdStyle.baseStyle,
    thead: theadStyle.baseStyle,
    tr: trStyle.baseStyle?.(props),
    globalActions: globalActionsStyle.baseStyle,
  }),
  defaultProps: {},
  sizes: {
    sm: {
      th: {...thStyle.sizes?.sm},
      td: {...tdStyle.sizes?.sm},
    },
    md: {
      th: {...thStyle.sizes?.md},
      td: {...tdStyle.sizes?.md},
    },
    lg: {
      th: {...thStyle.sizes?.lg},
      td: {...tdStyle.sizes?.lg},
    },
  },
});

export default TableTheme;

export const useTableStyleConfig = <Part extends (typeof tableAnatomy)[number]>(
  part: Part,
  props?: Parameters<typeof useMultiStyleConfig>[1],
) => {
  return useMultiStyleConfig(`Table`, props ?? {})?.[part] ?? ({} as any);
};
